import React, { useCallback, useState } from "react";
import Relay, { graphql, QueryRenderer } from "react-relay";

import { commitMutation } from "relay-runtime";

import { environment } from "~/models";
import { Button } from "~/ui";
import Checkbox from "~/ui/legacy/Checkbox";
import { Input, Modal } from "~/ui/legacyFellowIndex";
import toast from "~/ui/toast";

import type { ReuseQuestions_feedback$data as Feedback } from "~/__generated__/ReuseQuestions_feedback.graphql";
import type { ReuseQuestionsCreateDraftMutation } from "~/__generated__/ReuseQuestionsCreateDraftMutation.graphql";
import type { ReuseQuestionsFeedbackQuery } from "~/__generated__/ReuseQuestionsFeedbackQuery.graphql";
import type { ReuseQuestionsRequestQuery } from "~/__generated__/ReuseQuestionsRequestQuery.graphql";

import styles from "./css/ReuseQuestions.module.css";

const CreateDraftMutation = graphql`
	mutation ReuseQuestionsCreateDraftMutation($feedbackId: ID!, $title: String!, $useSubject: Boolean!) {
		createDraftFromFeedback(feedbackId: $feedbackId, title: $title, useSubject: $useSubject) {
			feedback {
				id
				info
			}
		}
	}
`;

interface Props {
	id: string;
	open: boolean;
	onComplete: (feedbackID: string) => void;
	onClose: () => void;
}

function ReuseQuestionsModal({
	feedback,
	open,
	onClose,
	onComplete,
}: Props & {
	feedback: Feedback;
}) {
	const [title, setTitle] = useState(feedback.title ? `Copy of ${feedback.title}` : "");
	const [useSubject, setUseSubject] = useState(false);
	const [started, setStarted] = useState(false);

	const toggleMeta = useCallback(() => {
		setUseSubject(useSubject => !useSubject);
	}, []);

	const onTitleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setTitle(event.target.value);
	}, []);

	const startDraft = useCallback(() => {
		if (feedback === undefined) return;
		setStarted(true);

		commitMutation<ReuseQuestionsCreateDraftMutation>(environment, {
			mutation: CreateDraftMutation,
			variables: {
				title,
				useSubject: feedback.isAnonymous ? false : useSubject,
				feedbackId: feedback.id,
			},
			onCompleted: ({ createDraftFromFeedback }, err) => {
				setStarted(false);
				if (err) toast.error(`Unable to reuse questions: ${err[0].message}`);
				if (createDraftFromFeedback) onComplete(createDraftFromFeedback.feedback.id);
			},
		});
	}, [feedback, onComplete, title, useSubject]);

	return (
		<Modal isOpen={open} onClose={onClose} width="24.5rem">
			<Modal.Header onClose={onClose}>
				<Modal.Title>Re-use questions</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<div className={styles.description}>Draft a new request using these questions</div>
				<div className={styles.title}>
					<div className={styles.placeholder}>New request title</div>
					<Input name="request_title" value={title} className={styles.input} onChange={onTitleChange} />
				</div>
				{!feedback.isAnonymous && (
					<div className={styles.reuse}>
						<Checkbox
							key={useSubject.toString()}
							label="Toggle all requestees"
							checked={useSubject}
							onChange={toggleMeta}
						/>
						<div className={styles.label} onClick={toggleMeta}>
							Keep the topic and recipients the same
						</div>
					</div>
				)}
			</Modal.Body>
			<Modal.Footer>
				<Button size="normal" onClick={onClose}>
					Cancel
				</Button>
				<Button size="normal" variant="primary" disabled={started} onClick={startDraft}>
					Start draft
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

const SmartReuseQuestionsModal = Relay.createFragmentContainer(ReuseQuestionsModal, {
	feedback: graphql`
		fragment ReuseQuestions_feedback on FeedbackNode {
			id
			title
			isAnonymous
		}
	`,
});

export function ReuseRequestQuestions(outerProps: Props) {
	return (
		<QueryRenderer<ReuseQuestionsRequestQuery>
			environment={environment}
			query={graphql`
				query ReuseQuestionsRequestQuery($id: ID!) {
					node(id: $id) {
						... on FeedbackRequestNode {
							feedback {
								...ReuseQuestions_feedback
							}
						}
					}
				}
			`}
			variables={{ id: outerProps.id }}
			// eslint-disable-next-line react/jsx-no-bind
			render={({ props }) => {
				if (!props) return null;

				const { node } = props;
				if (!node) return null;

				const { feedback } = node;
				if (!feedback) return null;

				return <SmartReuseQuestionsModal {...outerProps} feedback={feedback} />;
			}}
		/>
	);
}

export function ReuseFeedbackQuestions(outerProps: Props) {
	return (
		<QueryRenderer<ReuseQuestionsFeedbackQuery>
			environment={environment}
			query={graphql`
				query ReuseQuestionsFeedbackQuery($id: ID!) {
					node(id: $id) {
						... on FeedbackNode {
							...ReuseQuestions_feedback
						}
					}
				}
			`}
			variables={{ id: outerProps.id }}
			// eslint-disable-next-line react/jsx-no-bind
			render={({ props }) => {
				if (!props) return null;

				const { node } = props;
				if (!node) return null;

				return <SmartReuseQuestionsModal {...outerProps} feedback={node} />;
			}}
		/>
	);
}
